<template>
  <RydHeader :title="t('signIn.login.name')" />

  <div class="view-block px-4 pb-16 max-h-[100vh]">
    <form ref="loginForm" autocomplete="off" @submit.prevent="login()">
      <div>
        <div class="py-4">
          <RydInputText 
            id="loginEmail" 
            v-model:valueModel="model.login.email" 
            :label="t('account.email_label')"
            :hasError="!!customErrorEmail"
            :errorText="customErrorEmail"
            data-cy="loginEmailField"
            @input="resetErrors('email')" 
          />
        </div>
        <div class="relative py-4">
          <RydInputText 
            id="loginPassword" 
            v-model:valueModel="model.login.password"
            :label="t('account.password_label')" 
            :type="passwordType"
            :hasError="!!customErrorPassword"
            :errorText="customErrorPassword"
            autocomplete="current-password"
            data-cy="loginPasswordField"
            @input="resetErrors('password')"
            @click.prevent.stop
          />
          <button type="button" 
                  class="absolute right-1 bottom-8 in-car:bottom-8 in-car:h-10 in-car:w-10 cursor-pointer text-text-default"
                  @click.prevent="setPasswordType()"
                  @keydown.enter.prevent="setPasswordType()"
          >
            <RydIcon 
              :icon="passwordType === 'text' ? 'symbols/pw-show' : 'symbols/pw-hide'"
              isCustomColor
            />
          </button>
        </div>
        <RydButton link href="/forgotten-password">
          {{ t('account.forgotten_pwd') }}
        </RydButton>
        <RydButton type="submit" class="w-full mt-20">
          {{ t('signIn.login.name') }}
        </RydButton>
      </div>
    </form>

    <section class="w-full text-center">
      <Separator>{{ t('signIn.not_registered') }}</Separator>
      <RydButton theme="secondary"
                 name="reister"
                 class="w-full"
                 href="/register"
      >
        {{ t('signIn.go_register') }}
      </RydButton>
    </section>
  </div>
  
  <RydModalBottom v-if="loginSuccess" 
                  :title="t('signIn.login_success.title')"
                  icon="symbols/check-circle"
                  :description="t('signIn.login_success.text')"
                  @close="loginSuccess = false"
  > 
    <RydButton class="w-full mb-8" @click="loginSuccess = false">
      {{ t('signIn.login_success.button') }}
    </RydButton>
  </RydModalBottom>

  <RydModalBottom v-if="userShouldRegister" 
                  :title="t('signIn.register.dialog.title')"
                  icon="symbols/info-circle"
                  :description="t('signIn.register.dialog.content')"
                  @close="userShouldRegister = false"
  >
    <RydButton class="w-full mb-8" data-cy="loginNoAccountFoundClose" @click="handleRegisterNavigation">
      {{ t('account.register_cta') }}
    </RydButton>
  </RydModalBottom>

  <RydModalBottom v-if="errorAPI"
                  :title="t('errors.generic.title')"
                  :icon="'symbols/error-circle'"
                  :description="t('errors.generic.text')"
                  @close="errorAPI = null"
  >
    <RydButton class="w-full mb-8" @click="errorAPI = null">
      {{ t('gift_card.error.button_dismiss') }}
    </RydButton>
  </RydModalBottom>
</template>
<script setup lang="ts">
import RydButton from '@/components/RydButton/RydButton.vue';
import RydIcon from '@/components/RydIcon/RydIcon.vue';
import RydModalBottom from '@/components/RydModalBottom/RydModalBottom.vue';
import RydInputText from '@/components/RydInputText/RydInputText.vue';
import Separator from './components/Separator.vue';
import RydHeader from '@/components/RydHeader/RydHeader.vue';

import { useAuthStore } from '@/store/auth/auth';
import { checkAndApplyThemeURL, removeTheme } from '@/utils/utils';

import { AxiosError } from 'axios';
import { omit } from 'lodash';

import { onMounted, reactive, ref, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
// import { encryptOtp } from '@/api/auth/apiAuth';

const authState = useAuthStore();

const router = useRouter();
const route = useRoute();

const model = reactive({
  login: {
    email: null,
    password: null
  }
});

const errorAPI = ref(null);
const loginSuccess = ref(false);
const userShouldRegister = ref(false);
const loginForm = ref(null);
const customErrorEmail = ref(null);
const customErrorPassword = ref(null);

const passwordType = ref('password');
const setPasswordType = () => {
  passwordType.value = passwordType.value === 'password' ? 'text' : 'password';
};
onBeforeUnmount(() => removeTheme());


onMounted(async () => {
  model.login.email = null;
  model.login.password = null;
  checkAndApplyThemeURL();

  const emailWithPlus = route.query.email && (route.query.email as string).replaceAll(' ', '+');
  model.login.email = emailWithPlus || localStorage.getItem('lastUserEmail') || null;

  // if (route.query.encryptedPassword) {
  //   const encryptedPasswordWithPlus = route.query.email && (route.query.encryptedPassword as string).replaceAll(' ', '+');
  //   try {
  //     const decryptedPass = await encryptOtp(emailWithPlus, encryptedPasswordWithPlus);
  //     model.login.password = decryptedPass;
  //     login();
  //   } catch (error) {
  //     console.log(error);
  //   }
  // }
});

const checkEmailField = () => {
  if (!model.login.email?.length) {
    customErrorEmail.value = t('errors.required_error');
    return false;
  }
  if (!validateEmail(model.login.email)) {
    customErrorEmail.value = t('errors.email_not_valid');
    return false;
  }
  return true;
};

const validateEmail = (emailParam: string) => {
  const re = /\S+@\S+\.\S+/;
  return re.test(emailParam);
};

const checkFieldsOnSubmit = () => {
  checkEmailField();

  if (!model.login.password) {
    customErrorPassword.value = t('errors.required_error');
    return false;
  }
  return true;
};


const login = async () => {
  if (!checkFieldsOnSubmit()) return;
  try {
    authState.isLoading = true;
    await authState.login(model.login.email, model.login.password);

    await verifyQR();

    loginSuccess.value = true;

  } catch (e) {
    checkErrorMessage(e as AxiosError);
  } finally {
    authState.isLoading = false;
  }
};

const verifyQR = async () => {
  if (route.query.qrToken) {
    try {
      await authState.QRVerify(route.query.qrToken as string);
    } catch (e) {
      console.error('QR verification failed:', e);
      errorAPI.value = t('errors.generic.text');
      throw e;
    }
  }
};

const checkErrorMessage = (errorParam: AxiosError) => {
  const errorMap = {
    'wrong_login_details': () => errorAPI.value = t('errors.wrong_login_details'),
    'temp_pass': () => router.push({ path: '/change-tmp-password', query: { ...omit(route.query, 'encryptedPassword') } }),
    default: () => errorAPI.value = t('errors.generic.text')
  };
  const getError = errorMap[errorParam?.message as keyof typeof errorMap] || errorMap.default;
  getError();
};

// const loginSSO = async (token: string, provider: string) => {
//   try {
//     await authState.loginOpenId(token, provider);

//     trackSSOLogin(provider);

//     redirect();
//   } catch (e) {
//     if ((e as AxiosError).message === 'not_registered') {
//       userShouldRegister.value = true;
//       return;
//     }
//   }
// };


const handleRegisterNavigation = () => {
  userShouldRegister.value = false;
  router.replace('/register');
};

const resetErrors = (field: string) => {
  errorAPI.value = null;
  if (field === 'email') {
    customErrorEmail.value = '';
  } else if (field === 'password') {
    customErrorPassword.value = '';
  }
};
</script>
<style lang="scss" scoped>
.loading-spinner-button {
    animation: rotation 0.8s infinite linear;
  @keyframes rotation {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
}
</style>